import React, { useState, useEffect } from "react";
import { Container, Grid, Typography, InputLabel, FormControl, Select, MenuItem, Button, LinearProgress, Link, Stepper, Step, StepLabel } from "@material-ui/core";
import { GridTextField, HelpscoutFab, PasswordTextField } from "components";
import { useSnackbar } from "notistack";
import { getSportForms, SportForm } from "api/courts";
import { UserProfile } from "api/user";
import { UnConfrimedSignup } from "features/auth/reducer";
import authI18n from "features/auth/i18n";
import { postPendingDemo } from "api/clubs";
import config from "config";
import uI18n from "utils/i18n";
import i18n from "./i18n";
import ConfirmSignupDialog from "./ConfirmSignupDialog";

// prop types
interface RegisterClubViewProps {
    isCompany?: boolean,
    urlData: { [x: string]: string },
    userProfile: UserProfile | null,
    onShowLoginDialog: () => void,
    onSignOut: () => void,
    onSignUp: (email: string, password: string, firstName: string, lastName: string) => void,
    onResendConfirmationCode: (email: string) => void,
    onConfirmUser: (email: string, password: string, firstName: string, lastName: string, code: string, clubId?: string) => void,
}

// steps
enum Steps {
    ClubInformation = 0,
    AssignAddressee = 1,
    Done = 2,
}

// register club form view
const RegisterClubView = ({ isCompany, urlData, userProfile, onShowLoginDialog, onSignOut, onSignUp, onResendConfirmationCode, onConfirmUser }: RegisterClubViewProps) => {
    // snackbar
    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        authI18n.errors[error?.type] ? authI18n.errors[error.type] : `${i18n.anErrorOccurred}: ${error.description} (${error.type})`,
        { variant: "error" },
    );

    // ui state
    const [activeStep, setActiveStep] = useState(Steps.ClubInformation);
    const [city, setCity] = useState("");
    const [clubName, setClubName] = useState("");
    const [country, setCountry] = useState("Deutschland");
    const [emailAddress, setEmailAddress] = useState("");
    const [firstName, setFirstName] = useState("");
    const [generalSportForms, setGeneralSportForms] = useState<SportForm[]>([]);
    const [isLoading, setLoading] = useState(false);
    const [lastName, setLastName] = useState("");
    const [mainSportForm, setMainSportForm] = useState("");
    const [password, setPassword] = useState("");
    const [postalCode, setPostalCode] = useState("");
    const [showEmptyFields, setShowEmptyFields] = useState(false);
    const [stateOrProvince, setStateOrProvince] = useState("");
    const [street, setStreet] = useState("");
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);

    const processRegistration = async () => {
        if (!userProfile) {
            await confirmRegistration();
        } else {
            await completeRegistration();
        }
    };

    const confirmRegistration = async () => {
        setLoading(true);
        const signUpResult: UnConfrimedSignup = await onSignUp(emailAddress, password, firstName, lastName) as unknown as UnConfrimedSignup;
        if (signUpResult.userConfirmed === false) {
            setShowConfirmDialog(true);
        } else {
            await completeRegistration();
        }
    };

    const completeRegistration = async () => {
        setLoading(true);
        setShowConfirmDialog(false);
        try {
            // send request
            await postPendingDemo({
                city,
                clubName,
                country,
                mainSportForm: (isCompany ? "multisport" : mainSportForm),
                postalCode,
                stateOrProvince,
                street,
                clubType: (isCompany ? "company" : "standard"),
            });

            // finish up & redirect
            setActiveStep(Steps.Done);
            if (isCompany) {
                setTimeout(() => {
                    window.location.href = `${config.links.registerCompanyRedirect}`;
                }, 3000);
            } else {
                setTimeout(() => {
                    const params = Object.keys(urlData).map((k) => `${k}=${urlData[k]}`).join("&");
                    window.location.href = `${config.links.registerClubRedirect}${params ? `?${params}` : ""}`;
                }, 3000);
            }
        } catch (error) {
            displayError(error);
        } finally {
            setLoading(false);
        }
    };

    // fetch data
    const fetchGeneralSportForms = async () => { setGeneralSportForms(await getSportForms()); };
    useEffect(() => { fetchGeneralSportForms(); }, []);

    // render
    const capitalize = (word: string) => word.charAt(0).toUpperCase() + word.slice(1);
    return (
        <>
            <Container maxWidth="sm" style={{ marginTop: "32px" }}>
                {!isCompany && (<HelpscoutFab />)}
                <Grid container spacing={2}>

                    {/* header */}
                    <Grid item xs={12} style={{ textAlign: "center" }}>
                        <img src={isCompany ? "images/playsports-move_720px.png" : "images/playsports_logo.svg"} alt="PLAYSPORTS" style={{ height: "96px", margin: "32px" }} />
                        <Typography variant="h6">{isCompany ? i18n.registerCompany : i18n.registerClub}</Typography>
                    </Grid>

                    {/* progress */}
                    {activeStep !== 2 && (
                        <Grid item xs={12}>
                            <Stepper activeStep={activeStep} alternativeLabel>
                                {isCompany ? i18n.registerCompanySteps.map((label: string) => (
                                    <Step key={label}>
                                        <StepLabel>{label}</StepLabel>
                                    </Step>
                                )) : i18n.registerClubSteps.map((label: string) => (
                                    <Step key={label}>
                                        <StepLabel>{label}</StepLabel>
                                    </Step>
                                ))}
                            </Stepper>
                        </Grid>
                    )}

                    {/* 1. club information */}
                    {activeStep === Steps.ClubInformation && (
                        <>
                            <Grid item xs={12}>
                                <Typography align="center">{isCompany ? i18n.registerCompanyIntro : i18n.registerClubIntro}</Typography>
                            </Grid>
                            <GridTextField size={12} label={isCompany ? i18n.companyName : i18n.clubName} value={clubName} onChange={setClubName} />
                            {!isCompany && (
                                <Grid item xs={6}>
                                    <FormControl fullWidth variant="outlined" error={showEmptyFields && !mainSportForm}>
                                        <InputLabel>{i18n.mainSportForm}</InputLabel>
                                        <Select
                                            label={i18n.mainSportForm}
                                            value={mainSportForm}
                                            onChange={(e) => setMainSportForm(e.target.value as string)}
                                        >
                                            {generalSportForms.map((t) => (
                                                <MenuItem key={t.name} disabled={!t.available} value={t.name}>{uI18n.sportForms[t.name] || capitalize(t.name)}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}

                            <GridTextField size={isCompany ? 12 : 6} label={i18n.street} error={showEmptyFields && !street} value={street} onChange={setStreet} />
                            <GridTextField size={6} label={i18n.postalCode} error={showEmptyFields && !postalCode} value={postalCode} onChange={setPostalCode} />
                            <GridTextField size={6} label={i18n.city} error={showEmptyFields && !city} value={city} onChange={setCity} />
                            <GridTextField size={6} label={i18n.stateOrProvince} error={showEmptyFields && !stateOrProvince} value={stateOrProvince} onChange={setStateOrProvince} />
                            <GridTextField size={6} label={i18n.country} error={showEmptyFields && !country} value={country} onChange={setCountry} />
                            <Grid item xs={12} style={{ display: "flex", justifyContent: "center" }}>
                                <Button
                                    color="primary"
                                    disabled={!clubName}
                                    variant="contained"
                                    onClick={() => {
                                        if (!street || !postalCode || !city || !stateOrProvince || !country || (!isCompany && !mainSportForm)) {
                                            setShowEmptyFields(true);
                                        } else {
                                            setActiveStep(Steps.AssignAddressee);
                                        }
                                    }}
                                >
                                    {i18n.next}
                                </Button>
                            </Grid>
                        </>
                    )}

                    {/* 2. assign addressee */}
                    {activeStep === Steps.AssignAddressee && (
                        <>
                            {userProfile ? (
                                <Grid item xs={12}>
                                    <Typography>
                                        {`${i18n.loggedInAs(userProfile.email)} (`}
                                        <Link onClick={onSignOut} style={{ cursor: "pointer" }}>{i18n.useOtherAccount}</Link>
                                        )
                                    </Typography>
                                </Grid>
                            ) : (
                                <>
                                    <Grid item xs={12}>
                                        <Typography align="center">{i18n.createAccount}</Typography>
                                    </Grid>
                                    <GridTextField size={12} label={i18n.firstName} value={firstName} onChange={setFirstName} />
                                    <GridTextField size={12} label={i18n.lastName} value={lastName} onChange={setLastName} />
                                    <GridTextField size={12} label={i18n.emailAddress} value={emailAddress} onChange={setEmailAddress} />
                                    <Grid item xs={12}>
                                        <PasswordTextField label={i18n.password} value={password} margin="none" onChange={(e) => setPassword(e.target.value)} />
                                    </Grid>
                                    <Grid item xs={12} style={{ display: "flex", justifyContent: "center" }}>
                                        <Button onClick={onShowLoginDialog}>
                                            {i18n.useExistingAccount}
                                        </Button>
                                    </Grid>
                                </>
                            )}
                            <Grid item xs={12}>
                                {isLoading && <LinearProgress />}
                            </Grid>
                            <Grid item xs={12} style={{ display: "flex", justifyContent: "center" }}>
                                <Button onClick={() => setActiveStep(Steps.ClubInformation)} style={{ marginRight: "16px" }}>
                                    {i18n.back}
                                </Button>
                                <Button variant="contained" color="primary" disabled={!(userProfile || (firstName && lastName && emailAddress && password)) || isLoading} onClick={processRegistration}>
                                    {i18n.finish}
                                </Button>
                            </Grid>
                        </>
                    )}

                    {/* 3. done */}
                    {activeStep === Steps.Done && !isCompany && (
                        <Typography align="center">{i18n.whatsNext}</Typography>
                    )}
                    {activeStep === Steps.Done && isCompany && (
                        <div>
                            <Typography align="center">{i18n.whatsNextCompany}</Typography>
                        </div>
                    )}
                </Grid>
            </Container>
            {showConfirmDialog && (
                <ConfirmSignupDialog
                    email={emailAddress}
                    password={password}
                    firstName={firstName}
                    lastName={lastName}
                    showDialog={showConfirmDialog}
                    onResendConfirmationCode={onResendConfirmationCode}
                    onConfirmUser={onConfirmUser}
                    onCompleteRegistration={completeRegistration}
                />
            )}
        </>
    );
};

export default RegisterClubView;
